import { ExternalLink } from "lucide-react";
import { useGetList, useLocaleState, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { MatchPair, MatchPairSide } from "../types";
import { PhaseBadge } from "./PhaseBadge";
import { countryName, formatBudget, formatDay } from "./projectUtils";
import { useIsImportsOwner } from "./useIsImportsOwner";

// Paires que l'IA a laissées à un utilisateur ; les autres paires en attente attendent encore l'arbitrage de la nuit.
const LEFT_TO_USER = { status: "pending", arbitrated_by: "ai" };

/**
 * Paires de fiches que ni les règles ni l'IA n'ont tranchées : le responsable des imports dit si c'est le même
 * projet. Réservé à lui par la base (`is_imports_owner`), l'écran ne fait que l'annoncer.
 */
export const MatchPairList = () => {
  const translate = useTranslate();
  const owner = useIsImportsOwner();
  if (owner.isPending) return null;
  if (!owner.data) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        {translate("resources.projects.duplicates.forbidden")}
      </p>
    );
  }
  return <MatchPairReview />;
};

const MatchPairReview = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [busy, setBusy] = useState<string | null>(null);
  const {
    data: pairs,
    isPending,
    refetch,
  } = useGetList<MatchPair>("project_match_pairs", {
    filter: LEFT_TO_USER,
    pagination: { page: 1, perPage: 50 },
    sort: { field: "similarity", order: "DESC" },
  });

  // La décision est enregistrée tout de suite ; la fusion elle-même est faite par le worker au passage suivant.
  const decide = async (pair: MatchPair, sameProject: boolean) => {
    setBusy(String(pair.id));
    const { error } = await getSupabaseClient().rpc("decide_project_match", {
      pair_a: pair.project_a,
      pair_b: pair.project_b,
      same_project: sameProject,
    });
    setBusy(null);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify(
      `resources.projects.duplicates.${sameProject ? "merge_recorded" : "rejected"}`,
      { type: "info" },
    );
    refetch();
  };

  if (isPending) return null;
  return (
    <div className="mt-2 pb-4 flex flex-col gap-4 max-w-6xl">
      <div>
        <h2 className="text-xl font-semibold">
          {translate("resources.projects.duplicates.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.duplicates.intro")}
        </p>
      </div>
      {!pairs?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.duplicates.empty")}
        </p>
      ) : (
        pairs.map((pair) => (
          <Card key={pair.id}>
            <CardContent className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Side side={pair.a} />
                <Side side={pair.b} />
              </div>
              <div className="text-xs text-muted-foreground">
                {translate("resources.projects.duplicates.rule_note", {
                  similarity: Math.round(pair.similarity * 100),
                  reason: pair.reason,
                })}
                {pair.arbitration_note && (
                  <div className="text-inferred-ink mt-1">
                    {translate("resources.projects.duplicates.ai_note", {
                      confidence: Math.round(
                        (pair.arbitration_confidence ?? 0) * 100,
                      ),
                      note: pair.arbitration_note,
                    })}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={busy === pair.id}
                  onClick={() => decide(pair, true)}
                >
                  {translate("resources.projects.duplicates.same")}
                </Button>
                <Button
                  variant="outline"
                  disabled={busy === pair.id}
                  onClick={() => decide(pair, false)}
                >
                  {translate("resources.projects.duplicates.different")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

const Side = ({ side }: { side: MatchPairSide }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const facts = [
    [side.city, countryName(side.country, locale)].filter(Boolean).join(", "),
    side.owners,
    formatBudget(side, locale),
    [side.start_date, side.end_date].filter(Boolean).join(" → "),
  ].filter(Boolean);
  return (
    <div className="flex flex-col gap-2 min-w-0 text-sm">
      <div className="text-xs text-muted-foreground">
        {side.aegyra_project_id}
      </div>
      <Link
        to={`/projects/${side.id}/show`}
        className="font-semibold underline"
      >
        {side.name}
      </Link>
      <div>
        <PhaseBadge phase={side.phase} />
      </div>
      {facts.map((fact) => (
        <div key={fact}>{fact}</div>
      ))}
      {side.description && (
        <p className="text-muted-foreground">{side.description}</p>
      )}
      <ul className="flex flex-col gap-1">
        {side.documents.map((document) => (
          <li key={document.url} className="text-xs">
            <a
              href={document.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline inline-flex gap-1 items-start"
            >
              <span>
                {translate("resources.projects.duplicates.document", {
                  date: formatDay(document.date, locale),
                  title: document.title,
                })}
              </span>
              <ExternalLink className="h-3 w-3 mt-0.5 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Lien vers l'écran de validation, affiché au responsable des imports quand des paires lui sont laissées. */
export const MatchPairsButton = () => {
  const { data: owner } = useIsImportsOwner();
  return owner ? <MatchPairsCount /> : null;
};

const MatchPairsCount = () => {
  const translate = useTranslate();
  const { total } = useGetList<MatchPair>("project_match_pairs", {
    filter: LEFT_TO_USER,
    pagination: { page: 1, perPage: 1 },
    sort: { field: "similarity", order: "DESC" },
  });
  if (!total) return null;
  return (
    <Button asChild variant="outline" size="sm">
      <Link to="/project_match_pairs">
        {translate("resources.projects.duplicates.button", {
          smart_count: total,
        })}
      </Link>
    </Button>
  );
};
